import PropTypes from 'prop-types';
import React from 'react';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const isSameBonus = (a, b) => {
  if (a.id && b.id) return a.id === b.id;
  return a._key === b._key;
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--od-c-414651, #414651)",
  marginBottom: "4px",
  display: "block"
};

const inputStyle = {
  border: "1px solid var(--od-c-d5d7da, #d5d7da)",
  borderRadius: "8px",
  boxShadow: "0px 1px 2px 0px rgba(10, 13, 18, 0.05)",
  fontSize: "14px",
  height: "40px",
  padding: "8px 12px"
};

const toggleGroupStyle = {
  border: "1px solid var(--od-c-d5d7da, #d5d7da)",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0px 1px 2px 0px rgba(10, 13, 18, 0.05)",
  display: "inline-flex",
  flexShrink: 0
};

const toggleBtnStyle = (isActive, isDisabled) => ({
  background: isActive ? "var(--od-c-136395, #136395)" : "var(--od-c-ffffff, white)",
  border: "none",
  borderRight: "1px solid var(--od-c-d5d7da, #d5d7da)",
  padding: "0 12px",
  height: "36px",
  fontSize: "14px",
  fontWeight: 600,
  color: isActive ? "var(--od-c-ffffff, #ffffff)" : "var(--od-c-414651, #414651)",
  cursor: isDisabled ? "not-allowed" : "pointer",
  outline: "none",
  opacity: isDisabled ? 0.5 : 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const toggleBtnLastStyle = (isActive, isDisabled) => ({
  ...toggleBtnStyle(isActive, isDisabled),
  borderRight: "none"
});

const V3BudgetBonusRow = ({
  csrfToken,
  team,
  currentUser,
  budget,
  budgetBonus,
  deleteBudgetBonus,
  debouncedAutoSaveBudgetBonus,
  updateBudget,
  dealBrackets = []
}) => {
  const percentageIncompatible = !budget.from_dollar_one;
  const isPercentageBonus = budgetBonus.dollar_or_percentage_operator === "PERCENTAGE";
  const isCash = budgetBonus.dollar_or_percentage_operator === "CASH";

  const updateBonus = (attrs) => {
    var updatedBudgetBonus = Object.assign({}, budgetBonus, attrs);
    var budgetBonuses = [...budget.budget_bonuses].map((bb) => {
      if (isSameBonus(bb, budgetBonus)) {
        return updatedBudgetBonus;
      } else {
        return bb;
      }
    });
    var updatedBudget = Object.assign({}, budget, {
      budget_bonuses: budgetBonuses
    });

    updateBudget(updatedBudget);
    debouncedAutoSaveBudgetBonus(csrfToken, team, budgetBonus, attrs);
  };

  return (
    <div className="d-flex align-items-end mb-2" style={{ gap: '8px' }}>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <label style={labelStyle}>
          Ticket threshold
        </label>
        <MaskedInput
          className="form-control"
          style={inputStyle}
          mask={createNumberMask({ prefix: "", decimalLimit: 0 })}
          onFocus={(e) => e.target.select()}
          onChange={(e) => updateBonus({ ticket_count_requirement: e.target.value })}
          value={budgetBonus.ticket_count_requirement}
        />
      </div>

      <div style={{ alignSelf: 'flex-end', flexShrink: 0 }}>
        <div style={toggleGroupStyle}>
          <button
            type="button"
            style={toggleBtnStyle(isCash, false)}
            onClick={() => {
              if (isCash) return;
              updateBonus({
                dollar_or_percentage_operator: "CASH",
                upside_percentage: "",
                dollar_amount: budgetBonus.upside_percentage
              });
            }}
          >
            $
          </button>
          <button
            type="button"
            style={toggleBtnLastStyle(isPercentageBonus, percentageIncompatible)}
            disabled={percentageIncompatible}
            title={percentageIncompatible ? "Percentage bonuses are not compatible with Deal Brackets when 'From Dollar One' is disabled" : ""}
            onClick={() => {
              if (isPercentageBonus || percentageIncompatible) return;
              updateBonus({
                dollar_or_percentage_operator: "PERCENTAGE",
                upside_percentage: budgetBonus.dollar_amount,
                dollar_amount: ""
              });
            }}
          >
            %
          </button>
        </div>
      </div>

      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <label style={labelStyle}>
          {isCash ? "Bonus amount" : "Percentage of ticket sales"}
        </label>
        <CurrencyInput
          className="form-control"
          style={{
            ...inputStyle,
            width: '100%',
            opacity: (percentageIncompatible && isPercentageBonus ? "0.5" : "1")
          }}
          disabled={percentageIncompatible && isPercentageBonus}
          title={percentageIncompatible && isPercentageBonus ? "This percentage bonus is disabled because it's not compatible with Deal Brackets when 'From Dollar One' is disabled" : ""}
          value={isCash ? budgetBonus.dollar_amount : budgetBonus.upside_percentage}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            if (isCash) {
              updateBonus({ dollar_amount: e.target.value });
            } else {
              updateBonus({ upside_percentage: e.target.value });
            }
          }}
          maskOptions={{ prefix: "", integerLimit: null }}
        />
      </div>

      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          deleteBudgetBonus(csrfToken, team, budgetBonus);
        }}
        style={{ color: "var(--od-c-717680, #717680)", marginBottom: "10px" }}
      >
        <i className="far fa-trash" style={{ fontSize: '14px' }}></i>
      </a>
    </div>
  );
};

V3BudgetBonusRow.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  budgetBonus: PropTypes.object.isRequired,
  deleteBudgetBonus: PropTypes.func.isRequired,
  debouncedAutoSaveBudgetBonus: PropTypes.func,
  updateBudget: PropTypes.func.isRequired,
  dealBrackets: PropTypes.array
};

export default V3BudgetBonusRow;
